import { lazy, Suspense, useMemo } from 'react'
import Container from '../../components/Container/Container'
import Advantages from '../../components/section/Advantages/Advantages'
import BannersCategory from '../../components/section/BannersCategory/BannersCategory'
import HomeOffer from '../../components/section/HomeOffer/HomeOffer'
import Philosophy from '../../components/section/Philosophy/Philosophy'
import SliderProducts from '../../components/section/SliderProducts/SliderProducts'
import Vacancies from '../../components/section/Vacancies/Vacancies'
import ErrorWrapper from '../../components/ui/ErrorWrapper/ErrorWrapper'
import LazyInView from '../../components/ui/LazyInView/LazyInView'
import RouteFallback from '../../components/ui/RouteFallback/RouteFallback'
import SliderProductsSkeleton from '../../components/ui/SliderProductsSkeleton/SliderProductsSkeleton'
import { usePageMeta } from '../../hooks/usePageMeta'
import { useProducts } from '../../hooks/useProducts'
import { useCategoriesRedux } from '../../hooks/useCategoriesRedux'
import { HOME_DESCRIPTION, HOME_TITLE } from '../../utils/pageMeta'

const ShopsMapContainer = lazy(() =>
  import('../../components/section/ShopsMap/ShopsMapContainer').then(module => ({
    default: module.ShopsMapContainer,
  })),
)

const HomePage = () => {
  usePageMeta({
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    path: '/',
  })
  const {
    newProducts,
    coffeeProducts,
    dripProducts,
    capsuleProducts,
    loadingNew,
    loadingCoffee,
    loadingDrip,
    loadingCapsule,
    error,
  } = useProducts() // Викликаємо без параметрів для отримання всіх продуктів

  const { categories } = useCategoriesRedux()

  const getCategoryByAliases = useMemo(() => {
    return (aliases: string[]) => {
      const normalizedAliases = aliases.map(alias => alias.trim().toLowerCase())
      return categories.find(category =>
        normalizedAliases.includes(category.internalName.trim().toLowerCase()),
      )
    }
  }, [categories])

  const coffeeCategory = getCategoryByAliases(['coffee', 'kava'])
  const dripCategory = getCategoryByAliases(['drip', 'dripi'])
  const capsuleCategory = getCategoryByAliases(['kapsuli'])

  // Create banners dynamically from categories data
  const banners = useMemo(() => {
    const categoryBanners = categories
      .filter(cat => cat.showBanner === true)
      .map((category, index) => ({
        id: index + 1,
        image: category.bannerUrl || '/images/product.png', // Fallback if bannerUrl is missing
        title: category.externalName,
        icon: category.iconUrl,
        description: category.description,
        url: `/shop/${category.internalName}`,
        style: 'dark',
      }))

    return categoryBanners
  }, [categories])
  // const bannersNext = [
  //   {
  //     id: 4,
  //     image: '/images/banners/banner3.jpg',
  //     title: 'Акції',
  //     icon: '/images/banners/icon/icon3.svg',
  //     description: 'Вигідні пропозиції на твої улюблені позиції',
  //     url: '/shop/sales',
  //     style: 'light',
  //   },
  //   {
  //     id: 5,
  //     image: '/images/banners/banner3.jpg',
  //     title: 'Акції',
  //     icon: '/images/banners/icon/icon3.svg',
  //     description: 'Вигідні пропозиції на твої улюблені позиції',
  //     url: '/shop/sales',
  //     style: 'light',
  //   },
  // ]

  // Показуємо помилку тільки якщо є помилка з новинками
  if (error) {
    return (
      <Container>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            fontSize: '1.2rem',
            color: 'red',
          }}
        >
          Помилка: {error}
        </div>
      </Container>
    )
  }

  return (
    <>
      <Container>
        {/* SEO/AI-discovery: factual, network-level page header.
            The h1 is sr-only on purpose — see HomePage.module.css and
            src/index.css `.sr-only`. The paragraph below stays visible. */}
        <h1 className="sr-only">
          Seaters Coffee — мережа кавʼярень specialty coffee у Києві
        </h1>

        <HomeOffer />

        {/* Блок новинок */}
        <ErrorWrapper section="Новинки">
          {loadingNew ? (
            <SliderProductsSkeleton title="Поповнення в seaters" itemCount={3} />
          ) : (
            newProducts.length > 0 && (
              <SliderProducts
                buttonText="Весь асортимент"
                slug="shop"
                category="new"
                products={newProducts}
              />
            )
          )}
        </ErrorWrapper>

        <BannersCategory banners={banners} />

        {/* Блок кави */}
        <ErrorWrapper section="Кава">
          {loadingCoffee ? (
            <SliderProductsSkeleton title="Кава" itemCount={3} />
          ) : (
            coffeeProducts.length > 0 && (
              <SliderProducts
                buttonText="Вся кава"
                slug={`shop/${coffeeCategory?.internalName || 'kava'}`}
                category="kava"
                title={coffeeCategory?.externalName}
                products={coffeeProducts}
              />
            )
          )}
        </ErrorWrapper>

        {/* Блок дріпів */}
        <ErrorWrapper section="Дріпи">
          {loadingDrip ? (
            <SliderProductsSkeleton title="Дріпи" itemCount={3} />
          ) : (
            dripProducts.length > 0 && (
              <SliderProducts
                buttonText="Всі дріпи"
                slug={`shop/${dripCategory?.internalName || 'dripi'}`}
                category="dripi"
                title={dripCategory?.externalName}
                products={dripProducts}
              />
            )
          )}
        </ErrorWrapper>

        {/* Блок капсул */}
        <ErrorWrapper section="Капсули">
          {loadingCapsule ? (
            <SliderProductsSkeleton title="Капсули" itemCount={3} />
          ) : (
            capsuleProducts.length > 0 && (
              <SliderProducts
                buttonText="Всі капсули"
                slug={`shop/${capsuleCategory?.internalName || 'kapsuli'}`}
                category="kapsuli"
                title={capsuleCategory?.externalName}
                products={capsuleProducts}
              />
            )
          )}
        </ErrorWrapper>

        {/* <BannersCategory banners={bannersNext} /> */}
        <ErrorWrapper section="Карта магазинів">
          <LazyInView minHeight="80vh">
            <Suspense fallback={<RouteFallback />}>
              <ShopsMapContainer />
            </Suspense>
          </LazyInView>
        </ErrorWrapper>
        {/* <AboutUs /> */}
        <Advantages />
      </Container>
      <Philosophy />
      <Container>
        <Vacancies />
      </Container>
    </>
  )
}

export default HomePage
